/**
 * カウンター預入日（pickupDate）は「チェックイン日の当日または前日」のみ選択可能。
 * このモジュールはその制約に関わる日付ユーティリティを提供する。
 *
 * 日付はすべて `YYYY-MM-DD` 形式の文字列（＝日本時間のカレンダー日付）として扱う。
 * 内部で使う `Date` は日付演算のための一時的な入れ物で、タイムゾーン変換は行わない。
 */

/**
 * `YYYY-MM-DD` 形式の文字列を Date へ変換する。不正な場合は undefined。
 *
 * `new Date('YYYY-MM-DD')` は UTC 00:00 と解釈され、`getDate()` 等（ローカル基準）と
 * 混在させると日付が 1 日ずれ得るため、年月日を分解してローカル基準の Date を作る。
 * parse / format をローカル基準で統一することで往復のズレを防ぐ（TZ には非依存）。
 */
export const parseDateString = (value: string): Date | undefined => {
    const [year, month, day] = value.split('-').map(Number);
    if (year === undefined || month === undefined || day === undefined) return undefined;
    if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return undefined;
    return new Date(year, month - 1, day);
};

/** Date を `YYYY-MM-DD` 形式の文字列へ変換する（ローカル基準。parseDateString と対）。 */
export const toDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * pickupDate がチェックイン日の当日または前日かを判定する。
 * いずれかが空 / 不正フォーマットの場合は判定不能として true（範囲内扱い）を返す。
 */
export const isPickupDateWithinCheckIn = (checkInDate: string, pickupDate: string): boolean => {
    if (!checkInDate || !pickupDate) return true;

    const checkIn = parseDateString(checkInDate);
    if (!checkIn) return true;

    const previousDay = new Date(checkIn);
    previousDay.setDate(previousDay.getDate() - 1);

    return pickupDate === checkInDate || pickupDate === toDateString(previousDay);
};

/**
 * カレンダーで選択可能な pickupDate の範囲 `[min, max]` を求める。
 * - max = チェックイン日
 * - min = チェックイン前日（ただし過去日は選べないよう today を下限とする）
 *
 * checkInDate が未入力 / 不正な場合は today を下限とし、上限は設けない（従来挙動）。
 */
export const getPickupDateRange = (checkInDate: string, today: Date): { min: Date; max: Date | undefined } => {
    const checkIn = checkInDate ? parseDateString(checkInDate) : undefined;
    if (!checkIn) return { min: today, max: undefined };

    const previousDay = new Date(checkIn);
    previousDay.setDate(previousDay.getDate() - 1);

    const min = previousDay.getTime() < today.getTime() ? today : previousDay;
    return { min, max: checkIn };
};
